import {
  BulkTradeRequestStates,
  OptimizerRunV2Mode,
  OptimizerRunV2State,
  OrgBulkTradingProcesses,
} from '@generated/graphql';
import type { TFunction } from 'i18next';
import type { BulkRebalanceRun } from '../../types/run.types';
import type {
  ReviewTradeCounts,
  ReviewTradeEvaluateProcess,
} from '../../types/tradeReview.types';

export const resolveReviewEvaluateProcess = (
  _bulkTradingProcess?: OrgBulkTradingProcesses | string | null,
): ReviewTradeEvaluateProcess => OrgBulkTradingProcesses.Bucket;

export const shouldShowTradeReview = (
  run: BulkRebalanceRun | null,
  succeededCount: number,
): boolean =>
  !!run &&
  run.state === OptimizerRunV2State.Completed &&
  run.mode === OptimizerRunV2Mode.Live &&
  succeededCount > 0;

export const areReviewTradesFullyFetched = (
  fetchedRowCount: number,
  apiTotalCount: number,
): boolean => fetchedRowCount >= apiTotalCount;

/**
 * Apollo `pollInterval` refetches the original variables (offset 0) and
 * replaces the result. That wipes pages merged by `fetchMore`, so only poll
 * while the snapshot still fits on the first page.
 */
export const shouldPollReviewSubTradeRequests = (params: {
  showReview: boolean;
  hasBulkTradeRequest: boolean;
  tradesResolved: boolean;
  fetchedCount: number;
  totalCount: number;
  pageSize: number;
}): boolean => {
  if (
    !params.showReview ||
    !params.hasBulkTradeRequest ||
    params.tradesResolved
  ) {
    return false;
  }

  return (
    params.totalCount <= params.pageSize &&
    params.fetchedCount <= params.pageSize
  );
};

/**
 * Stop STR polling once a non-empty snapshot is settled, there is no BTR, or
 * an empty 0/0 list has been polled for `emptyPollWindowMs`. Empty lists keep
 * polling inside that window so late writes can land, then stop so we do not
 * hit the endpoint forever.
 */
export const shouldStopReviewTradePolling = (params: {
  noReviewBulkTradeRequest: boolean;
  tradesSettled: boolean;
  totalCount: number;
  emptySnapshotAgeMs?: number;
  emptyPollWindowMs: number;
}): boolean => {
  if (params.noReviewBulkTradeRequest) {
    return true;
  }

  if (params.tradesSettled && params.totalCount > 0) {
    return true;
  }

  return (
    params.totalCount === 0 &&
    (params.emptySnapshotAgeMs ?? 0) >= params.emptyPollWindowMs
  );
};

export const mergeSubTradeRequestPages = <T extends { id?: string | null }>(
  previous: T[],
  incoming: T[],
): T[] => {
  const seen = new Set<string>();
  const merged: T[] = [];

  for (const row of [...previous, ...incoming]) {
    if (!row.id || seen.has(row.id)) {
      continue;
    }

    seen.add(row.id);
    merged.push(row);
  }

  return merged;
};

/**
 * Settle from the STR query only. Report-table paging must not gate Proceed.
 * Any complete snapshot — empty or not — is only final after the caller
 * confirms the total, so late STR writes can still land.
 */
export const areReviewTradesSettled = (params: {
  tradesQueryReady: boolean;
  fetchedCount: number;
  totalCount: number;
  totalCountConfirmed: boolean;
}): boolean =>
  params.tradesQueryReady &&
  areReviewTradesFullyFetched(params.fetchedCount, params.totalCount) &&
  params.totalCountConfirmed;

export const canEvaluateReviewTrades = (params: {
  bulkTradeRequestId?: string;
  tradesSettled: boolean;
  fetchedCount: number;
  actionableCount: number;
  process?: ReviewTradeEvaluateProcess;
  requireApproval: boolean;
  unverifiedActionableCount: number;
  canWriteSubTradeRequest: boolean;
}): boolean =>
  Boolean(params.bulkTradeRequestId) &&
  params.tradesSettled &&
  params.fetchedCount > 0 &&
  params.actionableCount > 0 &&
  !!params.process &&
  params.canWriteSubTradeRequest &&
  (!params.requireApproval || params.unverifiedActionableCount === 0);

export const buildReviewEvaluateKey = (params: {
  runId?: string;
  bulkTradeRequestId?: string;
  actionableIds: string[];
  allActionableVerified: boolean;
  process?: ReviewTradeEvaluateProcess;
}): string | undefined => {
  if (!params.runId || !params.bulkTradeRequestId || !params.process) {
    return undefined;
  }

  return [
    params.runId,
    params.bulkTradeRequestId,
    params.process,
    params.allActionableVerified ? 'verified' : 'unverified',
    [...params.actionableIds].sort().join(','),
  ].join(':');
};

export const isReviewBulkTradeRequest = (params: {
  id?: string;
  sourceId?: string;
  runId?: string;
}): boolean => Boolean(params.id) && params.sourceId === params.runId;

export const isUsableBulkTradeRequest = (params: {
  id?: string;
  state?: string;
  sourceId?: string;
  runId?: string;
}): boolean =>
  isReviewBulkTradeRequest(params) &&
  params.state !== BulkTradeRequestStates.Canceled;

export const selectUsableBulkTradeRequest = <
  T extends { id?: string; state?: string; sourceId?: string },
>(
  requests: T[],
  runId?: string,
): T | undefined =>
  requests.find((request) =>
    isUsableBulkTradeRequest({
      id: request.id,
      state: request.state,
      sourceId: request.sourceId,
      runId,
    }),
  );

/** Prefer a live BTR; fall back to a canceled one so cancel-all can still render rows. */
export const selectReviewBulkTradeRequest = <
  T extends { id?: string; state?: string; sourceId?: string },
>(
  requests: T[],
  runId?: string,
): T | undefined =>
  selectUsableBulkTradeRequest(requests, runId) ??
  requests.find((request) =>
    isReviewBulkTradeRequest({
      id: request.id,
      sourceId: request.sourceId,
      runId,
    }),
  );

/**
 * Resolve once a usable BTR is found, once a complete list is only
 * canceled/unusable, or once an empty list has been polled through
 * `emptyPollWindowMs` (confirmed no BTR for this run).
 */
export const areBulkTradeRequestsResolved = (params: {
  queryReady: boolean;
  fetchedCount: number;
  totalCount: number;
  hasUsable: boolean;
  emptySnapshotAgeMs?: number;
  emptyPollWindowMs?: number;
}): boolean => {
  if (!params.queryReady) {
    return false;
  }

  if (params.hasUsable) {
    return true;
  }

  if (params.fetchedCount >= params.totalCount && params.totalCount > 0) {
    return true;
  }

  if (
    params.totalCount === 0 &&
    params.emptyPollWindowMs != null &&
    (params.emptySnapshotAgeMs ?? 0) >= params.emptyPollWindowMs
  ) {
    return true;
  }

  return false;
};

export const resolveTradeReviewProceedDisabledReason = (
  t: TFunction,
  params: {
    showReview: boolean;
    tradesSettled: boolean;
    evaluated: boolean;
    evaluating: boolean;
    canEvaluate: boolean;
    canWriteSubTradeRequest: boolean;
    counts: ReviewTradeCounts;
    requireApproval: boolean;
  },
): string | undefined => {
  if (!params.showReview) {
    return undefined;
  }

  if (!params.tradesSettled) {
    return t('reviewTrades.proceedWaiting');
  }

  if (params.counts.total === 0 || params.counts.actionable === 0) {
    return t('reviewTrades.proceedNoTrades');
  }

  if (params.requireApproval && params.counts.unverifiedActionable > 0) {
    return t('reviewTrades.proceedNeedsApproval');
  }

  if (params.evaluating || (params.canEvaluate && !params.evaluated)) {
    return t('reviewTrades.proceedEvaluating');
  }

  if (!params.evaluated && !params.canWriteSubTradeRequest) {
    return t('reviewTrades.proceedMissingEvaluatePermission');
  }

  if (!params.evaluated) {
    return t('reviewTrades.proceedEvaluating');
  }

  return undefined;
};

export const canProceedFromTradeReview = (params: {
  runCanProceed: boolean;
  tradesSettled: boolean;
  evaluated: boolean;
  evaluating: boolean;
  actionableCount: number;
  requireApproval: boolean;
  unverifiedActionableCount: number;
}): boolean =>
  params.runCanProceed &&
  params.tradesSettled &&
  params.evaluated &&
  !params.evaluating &&
  params.actionableCount > 0 &&
  (!params.requireApproval || params.unverifiedActionableCount === 0);
